import React from 'react';
import './DPA.css';

const DPA: React.FC = () => {
  return (
    <div className="dpa-container">
      <div className="dpa-content">
        <header className="dpa-header">
          <h1 className="dpa-title">Data Processing Addendum (DPA)</h1>
          <div className="dpa-meta">
            <p><strong>URL:</strong> <code>https://soundlegal.ai/dpa</code></p>
            <p><strong>Version:</strong> v1.0 • <strong>Last updated:</strong> 09/01/2025</p>
          </div>
        </header>

        <div className="dpa-body">
          <p className="dpa-intro">
            This Data Processing Addendum ("<strong>DPA</strong>") forms part of the agreement between <strong>SoundLegal, Inc.</strong> ("<strong>Processor</strong>") and the customer identified in the order or service terms ("<strong>Controller</strong>") for access to the Service (the "<strong>Agreement</strong>").
          </p>

          <section className="dpa-section">
            <h2>1. Scope & Roles</h2>
            <ul>
              <li><strong>Controller</strong> determines purposes/means of processing <strong>Customer Personal Data</strong> within Customer's documents, accounts, and configurations.</li>
              <li><strong>Processor</strong> processes solely <strong>on documented instructions</strong> (Agreement, this DPA, and in-product settings), including to provide the Service, ensure security, fulfill legal obligations, and (if enabled) perform <strong>training</strong> per Section 2.4.</li>
              <li>For CCPA/CPRA, SoundLegal acts as <strong>Service Provider</strong> (not "seller" or "third party").</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>2. Processing Details</h2>
            <ul>
              <li><strong>Subject matter:</strong> Operation of the Service (AI-enabled analysis/generation) for Customer.</li>
              <li><strong>Nature & purpose:</strong> Hosting; storage; parsing; tokenization; inference; display; analytics; support; security (e.g., abuse detection); and backups.</li>
              <li><strong>Duration:</strong> Term of the Agreement plus limited post-termination retention per Section 9.</li>
              <li><strong>Data subjects:</strong> Individuals referenced in Customer documents; authorized users; counterparties.</li>
              <li><strong>Categories:</strong> Names; business contact data; contract content referencing individuals; user credentials/usage logs.</li>
              <li><strong>Special categories:</strong> Not intended. If Controller inputs such data, Controller is responsible for the lawful basis and disclosures.</li>
              <li><strong>Training (opt-in only):</strong> Processor will not use Customer Personal Data to train underlying models <strong>unless Controller enables Training Consent</strong>. Consent is revocable prospectively.</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>3. Controller Instructions & Responsibilities</h2>
            <ul>
              <li>Controller is solely responsible for the lawfulness of data input, notices/consents to data subjects, and configuration of retention, deletion, and Training Consent.</li>
              <li>Controller will <strong>not</strong> upload unlawful content, malware, or data it lacks rights to process.</li>
              <li>Processor will promptly inform Controller if it believes an instruction violates law.</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>4. Confidentiality</h2>
            <p>Processor ensures persons authorized to process personal data are bound by <strong>confidentiality</strong> and receive <strong>privacy/security</strong> training.</p>
          </section>

          <section className="dpa-section">
            <h2>5. Security Measures</h2>
            <p>Processor implements and maintains the <strong>technical and organizational measures</strong> in <strong>Annex II</strong>, including <strong>TLS 1.3</strong>, <strong>AES-256</strong>, least-privilege, MFA for admin access, logging/monitoring, vulnerability management, backups/DR, and subprocessor oversight.</p>
          </section>

          <section className="dpa-section">
            <h2>6. Subprocessors</h2>
            <ul>
              <li>Controller grants <strong>general authorization</strong> to engage subprocessors listed at <strong><a href="https://soundlegal.ai/subprocessors" target="_blank" rel="noopener noreferrer">https://soundlegal.ai/subprocessors</a></strong>.</li>
              <li>Processor imposes data-protection terms <strong>no less protective</strong> than this DPA and remains <strong>liable</strong> for subprocessor acts/omissions.</li>
              <li><strong>Change notice & objections:</strong> As set forth on the Subprocessors page and the Agreement; unresolved reasonable objections permit termination of the affected Service with <strong>pro-rata refund</strong>.</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>7. Assistance</h2>
            <p>Taking into account the nature of processing, Processor will assist Controller with:</p>
            <ul>
              <li><strong>Data subject requests</strong> (access, correction, deletion, restriction, objection, portability) using available tools and reasonable efforts;</li>
              <li><strong>Security, breach notifications,</strong> and information needed for <strong>DPIAs</strong> and prior consultations.</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>8. Breach Notification</h2>
            <p>Processor will notify Controller <strong>without undue delay</strong> (and in any case consistent with applicable law) after confirming a <strong>Personal Data Breach</strong> involving Customer Personal Data, provide details as known, and <strong>cooperate</strong> on remediation and notifications.</p>
          </section>

          <section className="dpa-section">
            <h2>9. Return & Deletion</h2>
            <p>Upon termination/expiry or upon Controller's instruction, Processor will <strong>return</strong> or <strong>delete</strong> Customer Personal Data (at Controller's election), subject to legally required retention and backup rotation. Data remaining in immutable backups will be <strong>isolated</strong> and purged on scheduled cycles.</p>
          </section>

          <section className="dpa-section">
            <h2>10. International Transfers</h2>
            <p>Where processing involves transfer from the <strong>EEA/UK/Switzerland</strong> to countries without adequacy decisions, the parties incorporate:</p>
            <ul>
              <li><strong>EU SCCs (Module 2, Controller→Processor)</strong> (Commission Decision 2021/914) with Annexes populated by this DPA and Annexes I–III; and</li>
              <li><strong>UK IDTA</strong> or <strong>UK Addendum</strong> to the SCCs for UK transfers.</li>
            </ul>
            <p>Processor implements <strong>supplementary measures</strong> (e.g., encryption, access controls, government request scrutiny).</p>
          </section>

          <section className="dpa-section">
            <h2>11. Audits</h2>
            <p>Upon written request (not more than <strong>once annually</strong>, unless required by a competent authority or following a material incident), Processor will:</p>
            <ul>
              <li>Provide <strong>available reports</strong>/certifications (e.g., SOC 2 summary) and security responses; and</li>
              <li>Permit a focused audit by Controller or independent auditor under reasonable NDA, timing, and scope conditions that avoid undue burden or exposure of unrelated data.</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>12. Government & Third-Party Requests</h2>
            <p>Processor will, to the extent legally permitted, <strong>promptly notify</strong> Controller of third-party or government requests for Customer Personal Data and <strong>redirect</strong> such requests to Controller. Processor will not disclose data unless legally required.</p>
          </section>

          <section className="dpa-section">
            <h2>13. CCPA/CPRA Service-Provider Terms</h2>
            <p>Processor shall not <strong>sell</strong> or <strong>share</strong> personal information, shall not retain/use/disclose it for any purpose other than performing the Service or as permitted by law, and shall implement reasonable security procedures. Processor certifies its understanding and compliance.</p>
          </section>

          <section className="dpa-section">
            <h2>14. Liability; Precedence</h2>
            <p>The <strong>limitations of liability</strong> and <strong>dispute resolution</strong> in the Agreement apply to this DPA. In case of conflict, this DPA controls solely as to <strong>personal data processing</strong>.</p>
          </section>

          <section className="dpa-section">
            <h2>15. Term</h2>
            <p>This DPA is effective for the Agreement term and survives as necessary to effect Sections 7–12.</p>
          </section>

          <hr className="dpa-divider" />

          <section className="dpa-section">
            <h2>Annex I - Details of Processing (SCC/IDTA)</h2>
            <ul>
              <li><strong>Data exporter (Controller):</strong> Customer listed in the Order/Agreement. Contact: the admin email on Customer's account.</li>
              <li><strong>Data importer (Processor):</strong> SoundLegal, Inc. Contact: <strong>info@soundlegal.ai</strong>.</li>
              <li><strong>Subject matter & duration:</strong> As in Sections 2 and 9.</li>
              <li><strong>Nature & purpose:</strong> Hosting, storage, analysis, generation, security, support.</li>
              <li><strong>Categories of data subjects/data:</strong> As in Section 2.</li>
              <li><strong>Frequency:</strong> Continuous for the Agreement term.</li>
              <li><strong>Transfers:</strong> From EEA/UK/CH to U.S. and any other non-adequate locations required by subprocessors, with SCC/IDTA safeguards.</li>
              <li><strong>Competent supervisory authority:</strong> Determined under SCC Clause 13 based on exporter location.</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>Annex II - Technical & Organizational Security Measures</h2>
            <ul>
              <li><strong>Governance:</strong> Security policies; designated security lead; annual training; vendor risk management.</li>
              <li><strong>Access Management:</strong> Role-based access control; least privilege; MFA for admin; quarterly access reviews; immediate revocation on role change/termination.</li>
              <li><strong>Encryption:</strong> TLS 1.3 in transit; AES-256 at rest; KMS-managed keys; encrypted backups.</li>
              <li><strong>Network & Systems:</strong> Segmented VPCs; firewalls/security groups; hardened baselines; automated patching; EDR/IDS where appropriate.</li>
              <li><strong>Monitoring & Logging:</strong> Centralized logs; time-sync; tamper-resistant storage; alerting on anomalous access; on-call rotation.</li>
              <li><strong>Vulnerability Management:</strong> Regular scans; critical patch SLA; annual 3rd-party penetration testing; remediation tracking.</li>
              <li><strong>Application Security:</strong> SDLC controls; code review; secrets management; dependency scanning; environment isolation.</li>
              <li><strong>Data Resilience:</strong> Daily backups; restore testing; multi-AZ redundancy; defined RTO/RPO.</li>
              <li><strong>Physical Security:</strong> Cloud provider data-center controls; no on-prem production storage.</li>
              <li><strong>Incident Response:</strong> Documented plan; 24/7 escalation; forensic support; post-mortems and corrective actions.</li>
            </ul>
          </section>

          <section className="dpa-section">
            <h2>Annex III - Authorized Subprocessors (at Effective Date)</h2>
            <ol>
              <li><strong>Google Cloud Platform (Google LLC)</strong> - hosting, storage, networking, logging, backups.</li>
              <li><strong>Google Vertex AI / Gemini (Google LLC)</strong> - model inference.</li>
              <li><strong>Stripe, Inc.</strong> - payment processing and billing.</li>
            </ol>
            <blockquote className="dpa-note">
              The current list is maintained at <a href="https://soundlegal.ai/subprocessors" target="_blank" rel="noopener noreferrer">https://soundlegal.ai/subprocessors</a>.
            </blockquote>
          </section>

          <section className="dpa-section">
            <h2>Annex IV - UK Addendum (Summary)</h2>
            <p>For UK transfers, the <strong>UK International Data Transfer Addendum</strong> to the EU SCCs is incorporated. Tables 1–4 are populated by Annex I–III and this DPA. In case of conflict, the UK Addendum controls for UK transfers.</p>
          </section>

          <hr className="dpa-divider" />

          <section className="dpa-section">
            <h2>Contact (All Pages)</h2>
            <ul>
              <li><strong>General privacy & DPA:</strong> <strong>info@soundlegal.ai</strong></li>
              <li><strong>DMCA:</strong> <strong>info@soundlegal.ai</strong></li>
            </ul>
            <blockquote className="dpa-note">
              For legal service of process or mailed notices, use our Delaware registered agent on file with the Delaware Secretary of State (details provided upon request or via official records).
            </blockquote>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DPA;
